import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { VITAL_SIGNS, VITAL_SIGNS_IN_ORDER, type VitalKey, type VitalRuleKind } from '@wodi/domain';
import type { ClinicalRulesView, TriageSettingsView, VitalRuleView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  LoadingBlock,
  Notice,
  PageHeader,
  Select,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  approveTriageCategory,
  approveVitalRule,
  clinicalRulesQuery,
  replaceVitalRules,
  saveTriageCategory,
  saveTriageSettings,
  setVitalCoding,
} from '../api/triage.ts';
import { useOnline } from '../hooks/use-online.ts';
import { toasts } from '../toasts.ts';
import styles from '../triage/clinical-rules.module.css';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Clinical rules (docs/plans/phase1-triage.md §6.4; ADR 0013). The clinical
// lead enters limits and ranges with their source; another person approves them. Every
// change makes a rule provisional again, and the product says "Provisional" wherever a
// provisional rule decides something. Nothing is deleted: a removed rule is retired.

const KIND_LABELS: Record<VitalRuleKind, string> = {
  possible_limits: 'Possible limits (outside is refused)',
  reference_range: 'Reference range (outside is flagged)',
  critical_range: 'Critical range (outside interrupts)',
};

const KIND_SHORT: Record<VitalRuleKind, string> = {
  possible_limits: 'Possible limits',
  reference_range: 'Reference range',
  critical_range: 'Critical range',
};

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function ageText(from: number, to: number | null): string {
  if (from === 0 && to === null) return 'All ages';
  return to === null ? `From day ${String(from)}` : `Days ${String(from)} to ${String(to - 1)}`;
}

function bounds(rule: { low: string | null; high: string | null }, unit: string): string {
  if (rule.low !== null && rule.high !== null) return `${rule.low} to ${rule.high} ${unit}`;
  if (rule.low !== null) return `From ${rule.low} ${unit}`;
  return `Up to ${rule.high ?? ''} ${unit}`;
}

interface DraftRule {
  id?: string;
  kind: VitalRuleKind;
  ageFrom: string;
  ageTo: string;
  low: string;
  high: string;
  context: string;
  source: string;
}

function toDraft(rule: VitalRuleView): DraftRule {
  return {
    id: rule.id,
    kind: rule.kind,
    ageFrom: String(rule.ageFromDays),
    ageTo: rule.ageToDays === null ? '' : String(rule.ageToDays),
    low: rule.low ?? '',
    high: rule.high ?? '',
    context: rule.context ?? '',
    source: rule.source,
  };
}

function VitalEditor({
  vitalKey,
  rules,
  onDone,
}: {
  vitalKey: VitalKey;
  rules: readonly VitalRuleView[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const definition = VITAL_SIGNS[vitalKey];
  const [drafts, setDrafts] = useState<DraftRule[]>(rules.map(toDraft));
  const update = (i: number, patch: Partial<DraftRule>) => {
    setDrafts((d) => d.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const save = useMutation({
    mutationFn: () =>
      replaceVitalRules(vitalKey, {
        rules: drafts.map((d) => ({
          ...(d.id === undefined ? {} : { id: d.id }),
          kind: d.kind,
          ageFromDays: Number(d.ageFrom === '' ? '0' : d.ageFrom),
          ageToDays: d.ageTo.trim() === '' ? null : Number(d.ageTo),
          low: d.low.trim() === '' ? null : d.low.trim(),
          high: d.high.trim() === '' ? null : d.high.trim(),
          context: d.context === '' ? null : d.context,
          source: d.source.trim(),
        })),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(clinicalRulesQuery.queryKey, next);
      toasts.show({ title: 'Rules saved', description: 'They are provisional until approved.' });
      onDone();
    },
  });
  return (
    <div className={styles.editor}>
      {drafts.length === 0 ? <p className={styles.help}>No rules. Add one below.</p> : null}
      {drafts.map((d, i) => (
        <fieldset key={d.id ?? `new-${String(i)}`} className={styles.ruleRow}>
          <legend className={styles.legend}>{`Rule ${String(i + 1)}`}</legend>
          <Select
            label="Kind"
            dense
            options={Object.entries(KIND_LABELS).map(([id, label]) => ({ id, label }))}
            value={d.kind}
            isDisabled={d.id !== undefined}
            onChange={(v) => {
              update(i, { kind: v as VitalRuleKind });
            }}
          />
          <div className={styles.pair}>
            <TextField
              label="From age (days)"
              dense
              inputMode="numeric"
              value={d.ageFrom}
              onChange={(v) => {
                update(i, { ageFrom: v });
              }}
            />
            <TextField
              label="Up to age (days, not included)"
              description="Empty: no upper age."
              dense
              inputMode="numeric"
              value={d.ageTo}
              onChange={(v) => {
                update(i, { ageTo: v });
              }}
            />
          </div>
          <div className={styles.pair}>
            <TextField
              label="Low"
              unit={definition.unitDisplay}
              dense
              inputMode="decimal"
              value={d.low}
              onChange={(v) => {
                update(i, { low: v });
              }}
            />
            <TextField
              label="High"
              unit={definition.unitDisplay}
              dense
              inputMode="decimal"
              value={d.high}
              onChange={(v) => {
                update(i, { high: v });
              }}
            />
          </div>
          {definition.contexts.length === 0 ? null : (
            <Select
              label="When taken"
              dense
              options={[
                { id: 'any', label: 'Any' },
                ...definition.contexts.map((c) => ({
                  id: c,
                  label: c.charAt(0).toUpperCase() + c.slice(1),
                })),
              ]}
              value={d.context === '' ? 'any' : d.context}
              onChange={(v) => {
                update(i, { context: v === 'any' ? '' : String(v) });
              }}
            />
          )}
          <TextField
            label="Source"
            description="The guideline and edition the numbers come from."
            dense
            value={d.source}
            onChange={(v) => {
              update(i, { source: v });
            }}
          />
          <div>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setDrafts((all) => all.filter((_, j) => j !== i));
              }}
            >
              Remove rule
            </Button>
          </div>
        </fieldset>
      ))}
      <div className={styles.actions}>
        <Button
          dense
          onPress={() => {
            setDrafts((d) => [
              ...d,
              {
                kind: 'reference_range',
                ageFrom: '0',
                ageTo: '',
                low: '',
                high: '',
                context: '',
                source: '',
              },
            ]);
          }}
        >
          Add rule
        </Button>
        <Button
          dense
          variant="primary"
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save rules
        </Button>
        <Button dense variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, 'The rules could not be saved.')}</Notice>
      )}
    </div>
  );
}

function CodingEditor({ vitalKey, onDone }: { vitalKey: VitalKey; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [system, setSystem] = useState('');
  const [code, setCode] = useState('');
  const save = useMutation({
    mutationFn: () => setVitalCoding(vitalKey, system.trim(), code.trim()),
    onSuccess: (next) => {
      queryClient.setQueryData(clinicalRulesQuery.queryKey, next);
      toasts.show({ title: 'Code saved' });
      onDone();
    },
  });
  return (
    <div className={styles.editor}>
      <div className={styles.pair}>
        <TextField
          label="Code system key"
          description="As shown in Settings, Terminology, for example the loaded LOINC version's key."
          dense
          value={system}
          onChange={setSystem}
        />
        <TextField label="Code" dense value={code} onChange={setCode} />
      </div>
      <div className={styles.actions}>
        <Button
          dense
          variant="primary"
          isDisabled={system.trim() === '' || code.trim() === ''}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save code
        </Button>
        <Button dense variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, 'The code could not be saved.')}</Notice>
      )}
    </div>
  );
}

function VitalSection({
  vitalKey,
  view,
  mayManage,
  mayApprove,
  online,
}: {
  vitalKey: VitalKey;
  view: ClinicalRulesView;
  mayManage: boolean;
  mayApprove: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const definition = VITAL_SIGNS[vitalKey];
  const rules = view.rules.filter((r) => r.vitalKey === vitalKey);
  const coding = view.codings.find((c) => c.vitalKey === vitalKey && c.status === 'active');
  const [editing, setEditing] = useState<'rules' | 'coding' | null>(null);
  const approve = useMutation({
    mutationFn: approveVitalRule,
    onSuccess: (next) => {
      queryClient.setQueryData(clinicalRulesQuery.queryKey, next);
      toasts.show({ title: 'Rule approved' });
    },
  });
  const hasRanges = rules.some((r) => r.kind !== 'possible_limits');
  return (
    <section className={styles.vital} aria-labelledby={`vital-${vitalKey}`}>
      <div className={styles.vitalHeader}>
        <h3 id={`vital-${vitalKey}`} className={styles.vitalTitle}>
          {`${definition.label} (${definition.unitDisplay})`}
        </h3>
        {mayManage && editing === null ? (
          <span className={styles.actions}>
            <Button
              dense
              isDisabled={!online}
              onPress={() => {
                setEditing('rules');
              }}
            >
              Edit rules
            </Button>
            <Button
              dense
              variant="quiet"
              isDisabled={!online}
              onPress={() => {
                setEditing('coding');
              }}
            >
              Set code
            </Button>
          </span>
        ) : null}
      </div>
      <p className={styles.help}>
        {coding === undefined
          ? 'No code set: measurements are stored without a code.'
          : `Code ${coding.code} (${coding.codeSystemKey})${coding.display === null ? ', not in the active version' : `: ${coding.display}`}`}
      </p>
      {editing === 'rules' ? (
        <VitalEditor
          vitalKey={vitalKey}
          rules={rules}
          onDone={() => {
            setEditing(null);
          }}
        />
      ) : editing === 'coding' ? (
        <CodingEditor
          vitalKey={vitalKey}
          onDone={() => {
            setEditing(null);
          }}
        />
      ) : (
        <>
          {rules.length === 0 ? <p>No rules set.</p> : null}
          <ul className={styles.rules}>
            {rules.map((r) => (
              <li key={r.id} className={styles.rule}>
                <span className={styles.ruleText}>
                  {`${KIND_SHORT[r.kind]}: ${bounds(r, definition.unitDisplay)}, ${ageText(r.ageFromDays, r.ageToDays)}`}
                  {r.context === null ? '' : `, ${r.context}`}
                </span>
                <span className={styles.ruleSource}>{`Source: ${r.source}`}</span>
                <span className={styles.actions}>
                  {r.approval === 'approved' ? (
                    <Tag state="done">{`Approved by ${r.approvedBy?.displayName ?? ''}`}</Tag>
                  ) : (
                    <Tag state="pending">Provisional</Tag>
                  )}
                  {r.approval === 'provisional' && mayApprove ? (
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      isPending={approve.isPending && approve.variables === r.id}
                      pendingLabel="Approving…"
                      onPress={() => {
                        approve.mutate(r.id);
                      }}
                    >
                      Approve
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          {hasRanges ? null : (
            <p className={styles.help}>
              No ranges set. Values read "No range set" until ranges are added.
            </p>
          )}
          {approve.error === null ? null : (
            <Notice tone="error">
              {message(approve.error, 'The rule could not be approved.')}
            </Notice>
          )}
        </>
      )}
    </section>
  );
}

function CategoriesSection({
  view,
  mayManage,
  mayApprove,
  online,
}: {
  view: ClinicalRulesView;
  mayManage: boolean;
  mayApprove: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [display, setDisplay] = useState('');
  const [rank, setRank] = useState('');
  const [immediate, setImmediate] = useState(false);
  const system = view.categories[0]?.system ?? 'urn:wodi:local:triage-category';
  const set = (next: ClinicalRulesView) => {
    queryClient.setQueryData(clinicalRulesQuery.queryKey, next);
  };
  const approve = useMutation({
    mutationFn: approveTriageCategory,
    onSuccess: (next) => {
      set(next);
      toasts.show({ title: 'Category approved' });
    },
  });
  const add = useMutation({
    mutationFn: () =>
      saveTriageCategory({
        system,
        code: code.trim(),
        display: display.trim(),
        queueRank: Number(rank),
        requiresImmediateAttention: immediate,
        sortOrder: (view.categories.length + 1) * 10,
        status: 'active',
      }),
    onSuccess: (next) => {
      set(next);
      setAdding(false);
      setCode('');
      setDisplay('');
      setRank('');
      setImmediate(false);
      toasts.show({ title: 'Category saved' });
    },
  });
  return (
    <section aria-labelledby="categories-heading" className={styles.section}>
      <h2 id="categories-heading" className={styles.heading}>
        Triage categories
      </h2>
      <ul className={styles.rules}>
        {view.categories.map((c) => (
          <li key={c.id} className={styles.rule}>
            <span className={styles.ruleText}>
              {`${c.display}: order seen ${String(c.queueRank)}${c.requiresImmediateAttention ? ', needs a clinician now' : ''}`}
            </span>
            <span className={styles.actions}>
              {c.provisional ? (
                <Tag state="pending">Provisional</Tag>
              ) : (
                <Tag state="done">Approved</Tag>
              )}
              {c.provisional && mayApprove ? (
                <Button
                  dense
                  variant="quiet"
                  isDisabled={!online}
                  onPress={() => {
                    approve.mutate(c.id);
                  }}
                >
                  Approve
                </Button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {mayManage && !adding ? (
        <div>
          <Button
            dense
            isDisabled={!online}
            onPress={() => {
              setAdding(true);
            }}
          >
            Add category
          </Button>
        </div>
      ) : null}
      {adding ? (
        <div className={styles.editor}>
          <div className={styles.pair}>
            <TextField label="Name" dense value={display} onChange={setDisplay} />
            <TextField label="Code" dense value={code} onChange={setCode} />
          </div>
          <TextField
            label="Order seen (1 is first)"
            dense
            inputMode="numeric"
            value={rank}
            onChange={setRank}
          />
          <Checkbox isSelected={immediate} onChange={setImmediate}>
            Needs a clinician now
          </Checkbox>
          <div className={styles.actions}>
            <Button
              dense
              variant="primary"
              isDisabled={display.trim() === '' || code.trim() === '' || rank.trim() === ''}
              isPending={add.isPending}
              pendingLabel="Saving…"
              onPress={() => {
                add.mutate();
              }}
            >
              Save category
            </Button>
            <Button
              dense
              variant="quiet"
              onPress={() => {
                setAdding(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {(add.error ?? approve.error) === null ? null : (
        <Notice tone="error">
          {message(add.error ?? approve.error, 'The category could not be saved.')}
        </Notice>
      )}
    </section>
  );
}

function daysText(value: number | null): string {
  return value === null ? '' : String(value);
}

function SettingsSection({
  settings,
  mayManage,
  online,
}: {
  settings: TriageSettingsView;
  mayManage: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [weight, setWeight] = useState(daysText(settings.weightRequiredBelowAgeDays));
  const [muacFrom, setMuacFrom] = useState(daysText(settings.muacFromAgeDays));
  const [muacTo, setMuacTo] = useState(daysText(settings.muacToAgeDays));
  const [flagged, setFlagged] = useState(settings.queueFlaggedFirst);
  const num = (v: string) => (v.trim() === '' ? null : Number(v));
  const save = useMutation({
    mutationFn: () =>
      saveTriageSettings({
        weightRequiredBelowAgeDays: num(weight),
        muacFromAgeDays: num(muacFrom),
        muacToAgeDays: num(muacTo),
        queueFlaggedFirst: flagged,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(clinicalRulesQuery.queryKey, next);
      toasts.show({ title: 'Triage settings saved' });
    },
  });
  return (
    <section aria-labelledby="triage-settings-heading" className={styles.section}>
      <h2 id="triage-settings-heading" className={styles.heading}>
        Triage settings
      </h2>
      <TextField
        label="Ask for a weight below this age (days)"
        description="Empty: never asked. Saving triage without a weight then needs a reason."
        inputMode="numeric"
        isDisabled={!mayManage}
        value={weight}
        onChange={setWeight}
      />
      <div className={styles.pair}>
        <TextField
          label="Show MUAC from age (days)"
          inputMode="numeric"
          isDisabled={!mayManage}
          value={muacFrom}
          onChange={setMuacFrom}
        />
        <TextField
          label="Up to age (days, not included)"
          description="Both empty: MUAC is added by hand."
          inputMode="numeric"
          isDisabled={!mayManage}
          value={muacTo}
          onChange={setMuacTo}
        />
      </div>
      <Checkbox isSelected={flagged} isDisabled={!mayManage} onChange={setFlagged}>
        Patients with a priority flag go ahead of others in the same category
      </Checkbox>
      {mayManage ? (
        <div>
          <Button
            variant="primary"
            isDisabled={!online}
            isPending={save.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              save.mutate();
            }}
          >
            Save triage settings
          </Button>
        </div>
      ) : null}
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, 'The settings could not be saved.')}</Notice>
      )}
    </section>
  );
}

export function ClinicalRulesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const rules = useQuery(clinicalRulesQuery);
  const mayManage = session !== null && can(session, 'clinical_rules.manage');
  const mayApprove = session !== null && can(session, 'clinical_rules.approve');

  if (session === null || !can(session, 'clinical.read')) {
    return (
      <>
        <PageHeader title="Clinical rules" />
        <Notice tone="info">
          You don't have permission to see clinical rules. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <SettingsPage title="Clinical rules">
      {can(session, 'lab.read') ? (
        <p>
          Lab reference, critical and possible ranges are set on each analyte:{' '}
          <Link to="/settings/lab/analytes">Lab analytes and ranges</Link>.
        </p>
      ) : null}
      {!online ? (
        <Notice tone="info">
          Changing rules needs a connection to Wodi. Reconnect, then try again.
        </Notice>
      ) : null}
      {rules.isPending ? <LoadingBlock label="Loading clinical rules" rows={8} /> : null}
      {rules.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void rules.refetch()}>
              Try again
            </Button>
          }
        >
          {message(rules.error, 'The clinical rules could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {rules.isSuccess ? (
        <div className={styles.page}>
          {rules.data.rules.some((r) => r.approval === 'provisional') ||
          rules.data.categories.some((c) => c.provisional) ? (
            <Notice tone="info">
              Some clinical rules are provisional and haven't been approved by a clinical lead.
            </Notice>
          ) : null}
          <section aria-labelledby="vitals-heading" className={styles.section}>
            <h2 id="vitals-heading" className={styles.heading}>
              Measurements
            </h2>
            <p className={styles.help}>
              Possible limits refuse values outside them. Reference ranges flag values as High or
              Low. Critical ranges ask the nurse to tell a clinician. Ages are in days.
            </p>
            {VITAL_SIGNS_IN_ORDER.map((v) => (
              <VitalSection
                key={v.key}
                vitalKey={v.key}
                view={rules.data}
                mayManage={mayManage}
                mayApprove={mayApprove}
                online={online}
              />
            ))}
          </section>
          <CategoriesSection
            view={rules.data}
            mayManage={mayManage}
            mayApprove={mayApprove}
            online={online}
          />
          <SettingsSection
            key={JSON.stringify(rules.data.settings)}
            settings={rules.data.settings}
            mayManage={mayManage}
            online={online}
          />
        </div>
      ) : null}
    </SettingsPage>
  );
}
